import type { TrackingLink, ThemeClasses } from "../types";
import { useState } from "react";

interface TrackingLinkDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  trackingLink: TrackingLink | null;
  themeClasses: ThemeClasses;
  isDarkMode: boolean;
}

export function TrackingLinkDetailsModal({
  isOpen,
  onClose,
  trackingLink,
  themeClasses,
}: TrackingLinkDetailsModalProps) {
  const [activeTab, setActiveTab] = useState<"details" | "access">("details");

  if (!isOpen || !trackingLink) return null;

  const formatDate = (date: any) => {
    if (!date) return "N/A";
    try {
      const d = date._seconds ? new Date(date._seconds * 1000) : new Date(date);
      if (isNaN(d.getTime())) return "Fecha inválida";
      return d.toLocaleString("es-ES", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch (error) {
      console.error("Error formatting date:", error);
      return "Error en fecha";
    }
  };

  const getStatusColor = (status: any) => {
    if (!status) return "text-gray-600 bg-gray-100";
    
    // Usar el color del status si está definido
    if (status.color) return status.color;
    
    // Mapeo por ID para mayor precisión
    switch (status.id) {
      case "R5tW0uX4yY9cF6dH2aI8": // Active
        return "text-green-600 bg-green-100";
      case "W0yB5zC9dD4hK1iM7fN3": // Expired
        return "text-red-600 bg-red-100";
      case "S6uX1vY5zZ0dG7eI3bJ9": // Inactive
        return "text-gray-600 bg-gray-100";
      default:
        // Fallback por nombre
        switch (status.name) {
          case "active":
          case "Active":
            return "text-green-600 bg-green-100";
          case "inactive":
          case "Inactive":
            return "text-gray-600 bg-gray-100";
          case "expired":
          case "Expired":
            return "text-red-600 bg-red-100";
          case "pending":
          case "Pending":
            return "text-yellow-600 bg-yellow-100";
          default:
            return "text-gray-600 bg-gray-100";
        }
    }
  };

  const getStatusText = (status: any) => {
    if (!status) return "Desconocido";
    return status.name;
  };

  const getAccessTypeText = (type: string) => {
    switch (type) {
      case "public":
        return "Público";
      case "protected":
        return "Protegido";
      default:
        return type;
    }
  };

  const getActivationTypeText = (type: string) => {
    switch (type) {
      case "immediate":
        return "Inmediata";
      case "scheduled":
        return "Programada";
      case "inactive":
        return "Inactiva";
      default:
        return type;
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    // You could add a toast notification here
  };

  const uniqueIPs = trackingLink.accessHistory?.length || 0;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className={`w-full max-w-4xl max-h-[90vh] overflow-hidden rounded-xl ${themeClasses.cardBg} shadow-2xl`}>
        {/* Header */}
        <div className={`flex items-center justify-between p-4 sm:p-6 border-b ${themeClasses.cardBorder}`}>
          <h2 className={`text-lg sm:text-xl font-semibold ${themeClasses.text}`}>
            Detalles del Link de Seguimiento
          </h2>
          <button
            aria-label="Cerrar"
            onClick={onClose}
            className={`p-2 rounded-lg ${themeClasses.buttonHover} ${themeClasses.buttonText}`}
          >
            <i className="ri-close-line text-xl"></i>
          </button>
        </div>

        {/* Tabs */}
        <div className={`flex border-b ${themeClasses.cardBorder}`}>
          <button
            onClick={() => setActiveTab("details")}
            className={`px-4 sm:px-6 py-3 text-sm sm:text-base font-medium transition-colors ${
              activeTab === "details"
                ? `text-primary-600 border-b-2 border-primary-600`
                : `${themeClasses.textSecondary} hover:${themeClasses.text}`
            }`}
          >
            Información General
          </button>
          <button
            onClick={() => setActiveTab("access")}
            className={`px-4 sm:px-6 py-3 text-sm sm:text-base font-medium transition-colors relative ${
              activeTab === "access"
                ? `text-primary-600 border-b-2 border-primary-600`
                : `${themeClasses.textSecondary} hover:${themeClasses.text}`
            }`}
          >
            Historial de Accesos
            {trackingLink.accessHistory && trackingLink.accessHistory.length > 0 && (
              <span className="absolute -top-1 -right-2 bg-primary-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                {trackingLink.accessHistory.length}
              </span>
            )}
          </button>
        </div>

        {/* Content */}
        <div className="p-4 sm:p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          {activeTab === "details" ? (
            <div className="space-y-6">
              {/* Basic Information */}
              <div>
                <h3 className={`text-lg font-semibold ${themeClasses.text} mb-4`}>
                  Información Básica
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className={`text-sm font-medium ${themeClasses.textSecondary}`}>
                      Nombre del Link
                    </label>
                    <p className={`mt-1 ${themeClasses.text}`}>{trackingLink.name}</p>
                  </div>
                  <div>
                    <label className={`text-sm font-medium ${themeClasses.textSecondary}`}>
                      Estado
                    </label>
                    <div className="mt-1">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(trackingLink.status)}`}>
                        {getStatusText(trackingLink.status)}
                      </span>
                    </div>
                  </div>
                  <div>
                    <label className={`text-sm font-medium ${themeClasses.textSecondary}`}>
                      Tipo de Acceso
                    </label>
                    <p className={`mt-1 ${themeClasses.text}`}>
                      {getAccessTypeText(trackingLink.accessType)}
                      {trackingLink.hasAccessCode && (
                        <span className="ml-2 text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                          Con código
                        </span>
                      )}
                    </p>
                  </div>
                  <div>
                    <label className={`text-sm font-medium ${themeClasses.textSecondary}`}>
                      Tipo de Activación
                    </label>
                    <p className={`mt-1 ${themeClasses.text}`}>
                      {getActivationTypeText(trackingLink.activationType)}
                    </p>
                  </div>
                  <div>
                    <label className={`text-sm font-medium ${themeClasses.textSecondary}`}>
                      Total de Aperturas
                    </label>
                    <p className={`mt-1 ${themeClasses.text}`}>{(trackingLink.accessCount || 0).toLocaleString()}</p>
                  </div>
                  <div>
                    <label className={`text-sm font-medium ${themeClasses.textSecondary}`}>
                      Unidades Asignadas
                    </label>
                    <p className={`mt-1 ${themeClasses.text}`}>{trackingLink.unitsCount || 0} vehículos</p>
                  </div>
                </div>
              </div>

              {/* Dates */}
              <div>
                <h3 className={`text-lg font-semibold ${themeClasses.text} mb-4`}>
                  Fechas Importantes
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className={`text-sm font-medium ${themeClasses.textSecondary}`}>
                      Fecha de Creación
                    </label>
                    <p className={`mt-1 ${themeClasses.text}`}>{formatDate(trackingLink.createdAt)}</p>
                  </div>
                  <div>
                    <label className={`text-sm font-medium ${themeClasses.textSecondary}`}>
                      Fecha de Activación
                    </label>
                    <p className={`mt-1 ${themeClasses.text}`}>{formatDate(trackingLink.activationDate)}</p>
                  </div>
                  <div>
                    <label className={`text-sm font-medium ${themeClasses.textSecondary}`}>
                      Fecha de Expiración
                    </label>
                    <p className={`mt-1 ${themeClasses.text}`}>{formatDate(trackingLink.expirationDate)}</p>
                  </div>
                  <div>
                    <label className={`text-sm font-medium ${themeClasses.textSecondary}`}>
                      Última Actualización
                    </label>
                    <p className={`mt-1 ${themeClasses.text}`}>{formatDate(trackingLink.updatedAt)}</p>
                  </div>
                </div>
              </div>

              {/* Duration */}
              <div>
                <h3 className={`text-lg font-semibold ${themeClasses.text} mb-4`}>
                  Duración
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className={`text-sm font-medium ${themeClasses.textSecondary}`}>
                      Valor de Duración
                    </label>
                    <p className={`mt-1 ${themeClasses.text}`}>{trackingLink.durationValue || 'N/A'}</p>
                  </div>
                  <div>
                    <label className={`text-sm font-medium ${themeClasses.textSecondary}`}>
                      Unidad de Duración
                    </label>
                    <p className={`mt-1 ${themeClasses.text}`}>
                      {trackingLink.durationUnit === "hours" && "Horas"}
                      {trackingLink.durationUnit === "days" && "Días"}
                      {trackingLink.durationUnit === "weeks" && "Semanas"}
                      {trackingLink.durationUnit === "months" && "Meses"}
                      {!trackingLink.durationUnit && "N/A"}
                    </p>
                  </div>
                </div>
              </div>

              {/* URLs */}
              <div>
                <h3 className={`text-lg font-semibold ${themeClasses.text} mb-4`}>
                  URLs
                </h3>
                <div className="space-y-3">
                  <div>
                    <label className={`text-sm font-medium ${themeClasses.textSecondary}`}>
                      URL Pública
                    </label>
                    <div className="mt-1 flex items-center gap-2">
                      <input
                        type="text"
                        value={`${window.location.origin}${trackingLink.publicUrl}`}
                        readOnly
                        className={`flex-1 px-3 py-2 rounded-lg border ${themeClasses.cardBorder} ${themeClasses.text} ${themeClasses.cardBg}`}
                      />
                      <button
                        onClick={() => copyToClipboard(`${window.location.origin}${trackingLink.publicUrl}`)}
                        className="px-3 py-2 bg-primary-500 hover:bg-primary-400 text-white rounded-lg transition-colors"
                      >
                        <i className="ri-file-copy-line"></i>
                      </button>
                    </div>
                  </div>
                  { /* <div>
                    <label className={`text-sm font-medium ${themeClasses.textSecondary}`}>
                      Hash Único
                    </label>
                    <div className="mt-1 flex items-center gap-2">
                      <input
                        type="text"
                        value={trackingLink.uniqueHash}
                        readOnly
                        className={`flex-1 px-3 py-2 rounded-lg border ${themeClasses.cardBorder} ${themeClasses.text} ${themeClasses.cardBg}`}
                      />
                      <button
                        onClick={() => copyToClipboard(trackingLink.uniqueHash)}
                        className="px-3 py-2 bg-primary-500 hover:bg-primary-400 text-white rounded-lg transition-colors"
                      >
                        <i className="ri-file-copy-line"></i>
                      </button>
                    </div>
                  </div> */}
                </div>
              </div>

              {/* Internal Notes */}
              {trackingLink.internalNotes && (
                <div>
                  <h3 className={`text-lg font-semibold ${themeClasses.text} mb-4`}>
                    Notas Internas
                  </h3>
                  <div className={`p-4 rounded-lg border ${themeClasses.cardBorder} ${themeClasses.cardBg}`}>
                    <p className={`${themeClasses.text}`}>{trackingLink.internalNotes}</p>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-6">
              {/* Access Statistics */}
              <div>
                <h3 className={`text-lg font-semibold ${themeClasses.text} mb-4`}>
                  Estadísticas de Acceso
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className={`p-4 rounded-lg border ${themeClasses.cardBorder} ${themeClasses.cardBg}`}>
                    <div className={`text-2xl font-bold ${themeClasses.text}`}>
                      {(trackingLink.accessCount || 0).toLocaleString()}
                    </div>
                    <div className={`text-sm ${themeClasses.textSecondary}`}>Total de Aperturas</div>
                  </div>
                  <div className={`p-4 rounded-lg border ${themeClasses.cardBorder} ${themeClasses.cardBg}`}>
                    <div className={`text-2xl font-bold ${themeClasses.text}`}>
                      {uniqueIPs}
                    </div>
                    <div className={`text-sm ${themeClasses.textSecondary}`}>IPs Únicas</div>
                  </div>
                  { /* <div className={`p-4 rounded-lg border ${themeClasses.cardBorder} ${themeClasses.cardBg}`}>
                    <div className={`text-2xl font-bold ${themeClasses.text}`}>
                      {uniqueIPs > 0 ? Math.round(totalAccesses / uniqueIPs) : 0}
                    </div>
                    <div className={`text-sm ${themeClasses.textSecondary}`}>Promedio por IP</div>
                  </div> */}
                </div>
              </div>

              {/* Access History */}
              <div>
                <h3 className={`text-lg font-semibold ${themeClasses.text} mb-4`}>
                  Historial por Dirección IP
                </h3>
                {trackingLink.accessHistory && trackingLink.accessHistory.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className={`border-b ${themeClasses.cardBorder}`}>
                          <th className={`text-left py-3 px-4 ${themeClasses.textSecondary}`}>Dirección IP</th>
                          <th className={`text-left py-3 px-4 ${themeClasses.textSecondary}`}>Accesos</th>
                          <th className={`text-left py-3 px-4 ${themeClasses.textSecondary}`}>Primer Acceso</th>
                          <th className={`text-left py-3 px-4 ${themeClasses.textSecondary}`}>Último Acceso</th>
                          <th className={`text-left py-3 px-4 ${themeClasses.textSecondary}`}>User Agent</th>
                        </tr>
                      </thead>
                      <tbody>
                        {trackingLink.accessHistory.map((entry, index) => (
                          <tr key={index} className={`border-b ${themeClasses.cardBorder}`}>
                            <td className={`py-3 px-4 ${themeClasses.text}`}>
                              <div className="flex items-center gap-2">
                                <i className="ri-computer-line text-gray-400"></i>
                                <span className="font-mono text-sm">{entry.ip}</span>
                              </div>
                            </td>
                            <td className={`py-3 px-4 ${themeClasses.text}`}>
                              <span className="px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs font-medium">
                                {entry.accessCount}
                              </span>
                            </td>
                            <td className={`py-3 px-4 ${themeClasses.text} text-sm`}>
                              {formatDate(entry.firstAccess)}
                            </td>
                            <td className={`py-3 px-4 ${themeClasses.text} text-sm`}>
                              {formatDate(entry.lastAccess)}
                            </td>
                            <td className={`py-3 px-4 ${themeClasses.text} text-sm max-w-xs truncate`}>
                              <span title={entry.userAgent}>
                                {entry.userAgent.length > 50 
                                  ? `${entry.userAgent.substring(0, 50)}...` 
                                  : entry.userAgent}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className={`text-center py-8 ${themeClasses.textSecondary}`}>
                    <i className="ri-eye-off-line text-4xl mb-3"></i>
                    <p>No hay registros de acceso para este link</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className={`flex justify-end gap-3 p-4 sm:p-6 border-t ${themeClasses.cardBorder}`}>
          <button
            onClick={onClose}
            className={`px-4 py-2 rounded-lg border ${themeClasses.cardBorder} ${themeClasses.buttonText} ${themeClasses.buttonHover} transition-colors`}
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
}
